// Resume28.jsx - With Pagination and Complete Styling Controls
import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Divider,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Paper,
  IconButton,
} from '@mui/material';

import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import LanguageIcon from '@mui/icons-material/Language';
import DragIndicator from '@mui/icons-material/DragIndicator';
import Reorder from '@mui/icons-material/Reorder';
import ChevronLeft from '@mui/icons-material/ChevronLeft';
import ChevronRight from '@mui/icons-material/ChevronRight';

export const resumeMeta = {
  hasPhoto: false,
  columns: 1,
};

const A4_WIDTH_PX = 794;
const A4_HEIGHT_PX = 1123;
const LETTER_WIDTH_PX = 816;
const LETTER_HEIGHT_PX = 1056;

const defaultSectionOrder = [
  'header',
  'summary',
  'experience',
  'education',
  'skills',
  'certificates',
  'languages',
  'accomplishments',
  'additional-info',
  'software',
  'references',
  'volunteering',
  'interests',
  'websites',
];

const Resume28 = ({
  pageSize = 'A4',
  color = '#2196f3',
  font = 'Georgia, serif',
  fontSize = '14px',
  fontStyle = 'normal',
  headingSize = '18px',
  sectionSpacing = 30,
  paragraphSpacing = 12,
  lineSpacing = 22,
  topBottomMargin = 30,
  sideMargins = 30,
  lineWeight = 1,
  exporting = false,
  enableDragDrop = true,
  formData = {},
  linkedIn = '',
}) => {
  const pageW = pageSize === 'A4' ? A4_WIDTH_PX : LETTER_WIDTH_PX;
  const pageH = pageSize === 'A4' ? A4_HEIGHT_PX : LETTER_HEIGHT_PX;

  // ── Pagination states ────────────────────────────────────────────────────
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const measureRef = useRef(null);

  // ── States ────────────────────────────────────────────────────────────────
  const [personalData, setPersonalData] = useState({
    firstName: 'AVERY',
    lastName: 'DAVIS',
    city: '',
    phone: '',
    email: '',
    website: '',
    currentPosition: '',
    linkedIn: '',
  });

  const [savedSummary, setSavedSummary] = useState('');
  const [savedSkills, setSavedSkills] = useState([]);
  const [educationEntries, setEducationEntries] = useState([]);
  const [workExperiences, setWorkExperiences] = useState([]);
  const [savedCertifications, setSavedCertifications] = useState([]);
  const [savedLanguages, setSavedLanguages] = useState([]);
  const [savedAccomplishments, setSavedAccomplishments] = useState([]);
  const [savedAdditionalInfo, setSavedAdditionalInfo] = useState([]);
  const [savedSoftware, setSavedSoftware] = useState([]);
  const [savedReferences, setSavedReferences] = useState([]);
  const [savedVolunteering, setSavedVolunteering] = useState([]);
  const [savedInterests, setSavedInterests] = useState([]);
  const [savedWebsites, setSavedWebsites] = useState([]);
  const [customSections, setCustomSections] = useState({});

  const [sectionOrder, setSectionOrder] = useState(defaultSectionOrder);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [arrangeDialogOpen, setArrangeDialogOpen] = useState(false);

  // Safe JSON parse
  const safeJSON = (str, fallback = null) => {
    try { return JSON.parse(str) || fallback; } catch { return fallback; }
  };

  // ── Load all data from localStorage ──────────────────────────────────────
  useEffect(() => {
    const storedPersonal = localStorage.getItem('personalData');
    if (storedPersonal) {
      const data = safeJSON(storedPersonal, {});
      setPersonalData((prev) => ({
        ...prev,
        ...data,
        linkedIn: linkedIn || data.linkedIn || prev.linkedIn || '',
      }));
    }
    if (Object.keys(formData).length > 0) {
      setPersonalData((prev) => ({
        ...prev,
        ...formData,
        linkedIn: linkedIn || formData.linkedIn || prev.linkedIn || '',
      }));
    }

    const summaries = localStorage.getItem('summaries');
    if (summaries) {
      const arr = safeJSON(summaries, []);
      if (Array.isArray(arr) && arr.length > 0) setSavedSummary(arr[arr.length - 1]);
    }

    const edu = localStorage.getItem('educationEntries');
    if (edu) setEducationEntries(safeJSON(edu, []));

    const sk = localStorage.getItem('skills');
    if (sk) setSavedSkills(safeJSON(sk, []));

    const work = localStorage.getItem('workExperiences');
    if (work) setWorkExperiences(safeJSON(work, []));

    const cert = localStorage.getItem('certificationsList');
    if (cert) setSavedCertifications(safeJSON(cert, []));

    const lang = localStorage.getItem('languagesList');
    if (lang) setSavedLanguages(safeJSON(lang, []));

    const acc = localStorage.getItem('accomplishmentsList');
    if (acc) setSavedAccomplishments(safeJSON(acc, []));

    const add = localStorage.getItem('additionalInfo');
    if (add) setSavedAdditionalInfo(safeJSON(add, []));

    const soft = localStorage.getItem('softwareSkills');
    if (soft) setSavedSoftware(safeJSON(soft, []));

    const ref = localStorage.getItem('referencesList');
    if (ref) setSavedReferences(safeJSON(ref, []));

    const vol = localStorage.getItem('volunteering');
    if (vol) setSavedVolunteering(safeJSON(vol, []));

    const int = localStorage.getItem('interests');
    if (int) setSavedInterests(safeJSON(int, []));

    const web = localStorage.getItem('websites');
    if (web) setSavedWebsites(safeJSON(web, []));

    const userId = localStorage.getItem('userId');
    let sectionsData = {};
    if (userId) {
      const currentCustom = localStorage.getItem(`current_custom_sections_${userId}`);
      if (currentCustom) {
        const list = safeJSON(currentCustom, []);
        if (Array.isArray(list)) {
          list.forEach((name) => {
            const key = `custom_${userId}_${name}`;
            const dataStr = localStorage.getItem(key);
            if (dataStr) {
              const parsed = safeJSON(dataStr, {});
              const items = (parsed.items || []).filter((v) => v && v.trim());
              if (items.length) sectionsData[name] = items;
            }
          });
        }
      }
    }
    setCustomSections(sectionsData);

    const savedOrder = localStorage.getItem('resume28SectionOrder');
    if (savedOrder) {
      const order = safeJSON(savedOrder, defaultSectionOrder);
      if (Array.isArray(order) && !order.includes('websites')) {
        order.push('websites');
      }
      setSectionOrder(order);
    } else {
      setSectionOrder([
        ...defaultSectionOrder,
        ...Object.keys(sectionsData).map((n) => `custom_${n}`),
      ]);
    }
  }, [formData, linkedIn]);

  useEffect(() => {
    const customIds = Object.keys(customSections).map((n) => `custom_${n}`);
    setSectionOrder((prev) => {
      const base = prev.filter((s) => !s.startsWith('custom_'));
      const updated = [...base, ...customIds];
      if (JSON.stringify(updated) !== JSON.stringify(prev)) {
        localStorage.setItem('resume28SectionOrder', JSON.stringify(updated));
        return updated;
      }
      return prev;
    });
  }, [customSections]);

  useEffect(() => {
    if (sectionOrder.length > 0) {
      localStorage.setItem('resume28SectionOrder', JSON.stringify(sectionOrder));
    }
  }, [sectionOrder]);

  // ── Pagination calculation ────────────────────────────────────────────────
  useEffect(() => {
    if (exporting) return;
    const container = measureRef.current;
    if (!container) return;

    const usableHeight = pageH - topBottomMargin * 2;
    const sections = container.querySelectorAll('.resume28-section');
    let accumulated = 0;
    let pageCount = 1;

    sections.forEach((section) => {
      const h = section.getBoundingClientRect().height;
      if (accumulated + h > usableHeight * pageCount) {
        pageCount++;
      }
      accumulated += h;
    });

    setTotalPages(Math.max(1, pageCount));
  }, [
    sectionOrder, savedSummary, workExperiences, educationEntries,
    savedSkills, savedLanguages, savedAccomplishments, savedCertifications,
    savedReferences, savedSoftware, savedVolunteering, savedInterests,
    savedWebsites, savedAdditionalInfo, customSections,
    topBottomMargin, sectionSpacing, paragraphSpacing, lineSpacing,
    fontSize, headingSize, pageH, exporting, formData,
  ]);

  // ── Get sections for current page ────────────────────────────────────────
  const getSectionsForCurrentPage = () => {
    if (exporting || totalPages === 1) return sectionOrder;
    const container = measureRef.current;
    if (!container) return sectionOrder;

    const usableHeight = pageH - topBottomMargin * 2;
    const sections = container.querySelectorAll('.resume28-section');
    const sectionIds = [...sectionOrder];

    const pageChunks = [[]];
    let accumulated = 0;
    let pageIdx = 0;

    sections.forEach((section, i) => {
      const h = section.getBoundingClientRect().height;
      if (accumulated + h > usableHeight * (pageIdx + 1)) {
        pageIdx++;
        pageChunks.push([]);
      }
      if (sectionIds[i]) pageChunks[pageIdx].push(sectionIds[i]);
      accumulated += h;
    });

    return pageChunks[currentPage] || pageChunks[0] || sectionOrder;
  };

  // ── Drag & Drop ──────────────────────────────────────────────────────────
  const handleDragStart = (e, index) => {
    e.dataTransfer.setData('text/plain', index);
    e.currentTarget.style.opacity = '0.4';
  };
  const handleDragOver = (e, index) => { e.preventDefault(); setDragOverIndex(index); };
  const handleDrop = (e, index) => {
    e.preventDefault();
    const src = Number(e.dataTransfer.getData('text/plain'));
    if (src !== index) {
      const newOrder = [...sectionOrder];
      const [moved] = newOrder.splice(src, 1);
      newOrder.splice(index, 0, moved);
      setSectionOrder(newOrder);
    }
    setDragOverIndex(null);
  };
  const handleDragEnd = (e) => {
    e.currentTarget.style.opacity = '1';
    setDragOverIndex(null);
  };
  const resetSectionOrder = () => setSectionOrder(defaultSectionOrder);

  // ── Display values ────────────────────────────────────────────────────────
  const displayFirst = (formData.firstName || personalData.firstName || 'AVERY').toUpperCase();
  const displayLast = (formData.lastName || personalData.lastName || 'DAVIS').toUpperCase();
  const displayPosition = formData.currentPosition || personalData.currentPosition || '';
  const displayCity = formData.city || personalData.city || '';
  const displayEmail = formData.email || personalData.email || '';
  const displayPhone = formData.phone || personalData.phone || '';
  const displayLinkedIn = formData.linkedIn || personalData.linkedIn || linkedIn || '';
  const displayWebsite = formData.website || personalData.website || '';

  // ── Section heading — uses headingSize, font, fontStyle ──────────────────
  const SectionTitle = ({ children }) => (
    <Typography
      sx={{
        color: color,
        fontWeight: 700,
        fontSize: headingSize,
        letterSpacing: '0.1em',
        textTransform: 'uppercase',
        mb: `${paragraphSpacing * 0.5}px`,
        fontFamily: font,
        fontStyle: fontStyle,
      }}
    >
      {children}
    </Typography>
  );

  // ── Render each section ───────────────────────────────────────────────────
  const renderSection = (id) => {
    switch (id) {

      case 'header':
        return (
          <Box
            className="resume28-section"
            sx={{
              backgroundColor: color,
              color: 'white',
              px: `${sideMargins}px`,
              py: `${topBottomMargin * 0.7}px`,
              textAlign: 'center',
              mx: `-${sideMargins}px`,
              mt: `-${topBottomMargin}px`,
              mb: `${sectionSpacing * 0.5}px`,
            }}
          >
            <Typography
              sx={{
                fontWeight: 800,
                fontSize: headingSize,
                letterSpacing: '0.12em',
                lineHeight: 1.2,
                fontFamily: font,
                fontStyle: fontStyle,
              }}
            >
              {displayFirst} {displayLast}
            </Typography>
            {displayPosition && (
              <Typography
                sx={{
                  fontWeight: 300,
                  fontSize: fontSize,
                  letterSpacing: '0.1em',
                  mt: `${paragraphSpacing * 0.3}px`,
                  fontFamily: font,
                  fontStyle: fontStyle,
                }}
              >
                {displayPosition}
              </Typography>
            )}
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                flexWrap: 'wrap',
                gap: `${paragraphSpacing * 0.3}px 14px`,
                mt: `${paragraphSpacing * 0.8}px`,
              }}
            >
              {displayPhone && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                  <PhoneIcon sx={{ fontSize: fontSize }} />
                  <Typography sx={{ fontSize: fontSize, fontFamily: font }}>{displayPhone}</Typography>
                </Box>
              )}
              {displayEmail && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                  <EmailIcon sx={{ fontSize: fontSize }} />
                  <Typography sx={{ fontSize: fontSize, wordBreak: 'break-all', fontFamily: font }}>{displayEmail}</Typography>
                </Box>
              )}
              {displayCity && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                  <LocationOnIcon sx={{ fontSize: fontSize }} />
                  <Typography sx={{ fontSize: fontSize, fontFamily: font }}>{displayCity}</Typography>
                </Box>
              )}
              {displayLinkedIn && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                  <LinkedInIcon sx={{ fontSize: fontSize }} />
                  <Typography sx={{ fontSize: fontSize, wordBreak: 'break-all', fontFamily: font }}>{displayLinkedIn}</Typography>
                </Box>
              )}
              {displayWebsite && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                  <LanguageIcon sx={{ fontSize: fontSize }} />
                  <Typography sx={{ fontSize: fontSize, wordBreak: 'break-all', fontFamily: font }}>{displayWebsite}</Typography>
                </Box>
              )}
            </Box>
          </Box>
        );

      case 'summary':
        if (!savedSummary) return null;
        return (
          <Box className="resume28-section" mb={`${sectionSpacing}px`}>
            <SectionTitle>Summary</SectionTitle>
            <Divider sx={{ mb: `${paragraphSpacing * 0.5}px`, borderColor: color, borderBottomWidth: `${lineWeight}px` }} />
            <Typography
              sx={{
                fontSize: fontSize,
                lineHeight: `${lineSpacing}px`,
                fontFamily: font,
                fontStyle: fontStyle,
                textAlign: 'justify',
                wordBreak: 'break-word',
              }}
            >
              {savedSummary}
            </Typography>
          </Box>
        );

      case 'experience':
        if (!workExperiences.length) return null;
        return (
          <Box className="resume28-section" mb={`${sectionSpacing}px`}>
            <SectionTitle>Work Experience</SectionTitle>
            <Divider sx={{ mb: `${paragraphSpacing * 0.5}px`, borderColor: color, borderBottomWidth: `${lineWeight}px` }} />
            {workExperiences.map((exp, i) => (
              <Box key={i} mb={`${paragraphSpacing}px`}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '2px' }}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      sx={{
                        fontWeight: 700,
                        fontSize: fontSize,
                        lineHeight: `${lineSpacing}px`,
                        fontFamily: font,
                        fontStyle: fontStyle,
                        wordBreak: 'break-word',
                      }}
                    >
                      {exp.jobTitle || exp.title || 'Position'}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: fontSize,
                        color: 'text.secondary',
                        lineHeight: `${lineSpacing}px`,
                        fontFamily: font,
                        wordBreak: 'break-word',
                      }}
                    >
                      {[exp.employer || exp.company, exp.city, exp.country].filter(Boolean).join(', ') || 'Company'}
                    </Typography>
                  </Box>
                  <Typography
                    sx={{
                      fontSize: fontSize,
                      color: 'text.secondary',
                      fontStyle: 'italic',
                      whiteSpace: 'nowrap',
                      flexShrink: 0,
                      ml: '8px',
                      lineHeight: `${lineSpacing}px`,
                      fontFamily: font,
                    }}
                  >
                    {[exp.startMonth, exp.startYear].filter(Boolean).join(' ')}
                    {(exp.startMonth || exp.startYear) ? ' – ' : ''}
                    {exp.current ? 'Present' : [exp.endMonth, exp.endYear].filter(Boolean).join(' ')}
                  </Typography>
                </Box>
                {exp.description && (
                  <Box sx={{ mt: `${paragraphSpacing * 0.3}px` }}>
                    {exp.description.split('\n').filter((l) => l.trim()).map((line, j) => (
                      <Box key={j} sx={{ display: 'flex', alignItems: 'flex-start', mb: '2px' }}>
                        <Box sx={{ width: '4px', height: '4px', borderRadius: '50%', bgcolor: color, flexShrink: 0, mt: '5.5px', mr: '6px' }} />
                        <Typography
                          sx={{
                            fontSize: fontSize,
                            lineHeight: `${lineSpacing}px`,
                            fontFamily: font,
                            fontStyle: fontStyle,
                            wordBreak: 'break-word',
                            flex: 1,
                          }}
                        >
                          {line.trim().replace(/^[•\-]\s*/, '')}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>
            ))}
          </Box>
        );

      case 'education':
        if (!educationEntries.length) return null;
        return (
          <Box className="resume28-section" mb={`${sectionSpacing}px`}>
            <SectionTitle>Education</SectionTitle>
            <Divider sx={{ mb: `${paragraphSpacing * 0.5}px`, borderColor: color, borderBottomWidth: `${lineWeight}px` }} />
            {educationEntries.map((edu, i) => (
              <Box key={i} mb={`${paragraphSpacing}px`}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '2px' }}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      sx={{
                        fontWeight: 700,
                        fontSize: fontSize,
                        lineHeight: `${lineSpacing}px`,
                        fontFamily: font,
                        fontStyle: fontStyle,
                        wordBreak: 'break-word',
                      }}
                    >
                      {edu.degreeName || edu.degree || 'Degree'}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: fontSize,
                        color: 'text.secondary',
                        lineHeight: `${lineSpacing}px`,
                        fontFamily: font,
                        wordBreak: 'break-word',
                      }}
                    >
                      {[edu.collegeName || edu.schoolName, edu.collegeLocation || edu.schoolLocation].filter(Boolean).join(', ') || 'Institution'}
                    </Typography>
                  </Box>
                  <Typography
                    sx={{
                      fontSize: fontSize,
                      color: 'text.secondary',
                      fontStyle: 'italic',
                      whiteSpace: 'nowrap',
                      flexShrink: 0,
                      ml: '8px',
                      lineHeight: `${lineSpacing}px`,
                      fontFamily: font,
                    }}
                  >
                    {[edu.gradMonth, edu.gradYear].filter(Boolean).join(' ')}
                  </Typography>
                </Box>
                <Box sx={{ pl: '8px', mt: '2px' }}>
                  {edu.cgpa && <Typography sx={{ fontSize: fontSize, lineHeight: `${lineSpacing}px`, fontFamily: font }}>CGPA: {edu.cgpa}</Typography>}
                  {edu.percentage && <Typography sx={{ fontSize: fontSize, lineHeight: `${lineSpacing}px`, fontFamily: font }}>Percentage: {edu.percentage}%</Typography>}
                  {edu.specialization && <Typography sx={{ fontSize: fontSize, lineHeight: `${lineSpacing}px`, fontFamily: font }}>{edu.specialization}</Typography>}
                </Box>
              </Box>
            ))}
          </Box>
        );

      case 'skills':
        if (!savedSkills.length) return null;
        return (
          <Box className="resume28-section" mb={`${sectionSpacing}px`}>
            <SectionTitle>Skills</SectionTitle>
            <Divider sx={{ mb: `${paragraphSpacing * 0.5}px`, borderColor: color, borderBottomWidth: `${lineWeight}px` }} />
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: `${paragraphSpacing * 0.5}px` }}>
              {savedSkills.map((skill, i) => {
                const name = typeof skill === 'string' ? skill : (skill.name || skill.category || skill.skill || '');
                if (!name) return null;
                return (
                  <Chip
                    key={i}
                    label={name}
                    size="small"
                    sx={{
                      fontSize: fontSize,
                      height: '22px',
                      bgcolor: `${color}18`,
                      color: color,
                      border: `${lineWeight}px solid ${color}55`,
                      fontFamily: font,
                      fontWeight: 600,
                    }}
                  />
                );
              })}
            </Box>
          </Box>
        );

      case 'certificates':
      case 'certifications':
        if (!savedCertifications.length) return null;
        return (
          <Box className="resume28-section" mb={`${sectionSpacing}px`}>
            <SectionTitle>Certifications</SectionTitle>
            <Divider sx={{ mb: `${paragraphSpacing * 0.5}px`, borderColor: color, borderBottomWidth: `${lineWeight}px` }} />
            {savedCertifications.map((c, i) => (
              <Box key={i} mb={`${paragraphSpacing}px`}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '2px' }}>
                  <Typography
                    sx={{
                      fontWeight: 700,
                      fontSize: fontSize,
                      lineHeight: `${lineSpacing}px`,
                      fontFamily: font,
                      fontStyle: fontStyle,
                      wordBreak: 'break-word',
                      flex: 1,
                    }}
                  >
                    {c.name || c.certName || c.title || ''}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: fontSize,
                      color: 'text.secondary',
                      fontStyle: 'italic',
                      flexShrink: 0,
                      ml: '8px',
                      fontFamily: font,
                    }}
                  >
                    {c.year || c.date || ''}
                  </Typography>
                </Box>
                {(c.provider || c.organization || c.description) && (
                  <Typography
                    sx={{
                      fontSize: fontSize,
                      color: 'text.secondary',
                      lineHeight: `${lineSpacing}px`,
                      fontFamily: font,
                    }}
                  >
                    {[c.provider || c.organization, c.description].filter(Boolean).join(' • ')}
                  </Typography>
                )}
              </Box>
            ))}
          </Box>
        );

      case 'languages':
        if (!savedLanguages.length) return null;
        return (
          <Box className="resume28-section" mb={`${sectionSpacing}px`}>
            <SectionTitle>Languages</SectionTitle>
            <Divider sx={{ mb: `${paragraphSpacing * 0.5}px`, borderColor: color, borderBottomWidth: `${lineWeight}px` }} />
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: `${paragraphSpacing * 0.3}px 24px` }}>
              {savedLanguages.map((lang, i) => {
                const name = typeof lang === 'string' ? lang : (lang.language || lang.name || '');
                const level = typeof lang === 'object' ? (lang.level || lang.proficiency || '') : '';
                return (
                  <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: '5px', minWidth: '120px' }}>
                    <Box sx={{ width: '4px', height: '4px', borderRadius: '50%', bgcolor: color, flexShrink: 0 }} />
                    <Typography sx={{ fontSize: fontSize, lineHeight: `${lineSpacing}px`, fontFamily: font, fontStyle: fontStyle }}>
                      <span style={{ fontWeight: 600 }}>{name}</span>
                      {level ? ` — ${level}` : ''}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          </Box>
        );

      case 'accomplishments':
        if (!savedAccomplishments.length) return null;
        return (
          <Box className="resume28-section" mb={`${sectionSpacing}px`}>
            <SectionTitle>Accomplishments</SectionTitle>
            <Divider sx={{ mb: `${paragraphSpacing * 0.5}px`, borderColor: color, borderBottomWidth: `${lineWeight}px` }} />
            {savedAccomplishments.map((item, i) => {
              const text = typeof item === 'string' ? item : (item.title || item.description || item.name || '');
              return (
                <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', mb: '3px' }}>
                  <Box sx={{ width: '4px', height: '4px', borderRadius: '50%', bgcolor: color, flexShrink: 0, mt: '5.5px', mr: '6px' }} />
                  <Typography sx={{ fontSize: fontSize, lineHeight: `${lineSpacing}px`, fontFamily: font, fontStyle: fontStyle, wordBreak: 'break-word', flex: 1 }}>
                    {text}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        );

      case 'additional-info':
        if (!savedAdditionalInfo.length) return null;
        return (
          <Box className="resume28-section" mb={`${sectionSpacing}px`}>
            <SectionTitle>Additional Information</SectionTitle>
            <Divider sx={{ mb: `${paragraphSpacing * 0.5}px`, borderColor: color, borderBottomWidth: `${lineWeight}px` }} />
            {savedAdditionalInfo.map((item, i) => {
              const text = typeof item === 'string' ? item : (item.info || item.text || item.description || '');
              return (
                <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', mb: '3px' }}>
                  <Box sx={{ width: '4px', height: '4px', borderRadius: '50%', bgcolor: color, flexShrink: 0, mt: '5.5px', mr: '6px' }} />
                  <Typography sx={{ fontSize: fontSize, lineHeight: `${lineSpacing}px`, fontFamily: font, fontStyle: fontStyle, wordBreak: 'break-word', flex: 1 }}>
                    {text}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        );

      case 'software':
        if (!savedSoftware.length) return null;
        return (
          <Box className="resume28-section" mb={`${sectionSpacing}px`}>
            <SectionTitle>Software</SectionTitle>
            <Divider sx={{ mb: `${paragraphSpacing * 0.5}px`, borderColor: color, borderBottomWidth: `${lineWeight}px` }} />
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: `${paragraphSpacing * 0.5}px` }}>
              {savedSoftware.map((item, i) => {
                const name = typeof item === 'string' ? item : (item.name || item.software || item.tool || '');
                return (
                  <Chip
                    key={i}
                    label={name}
                    size="small"
                    sx={{
                      fontSize: fontSize,
                      height: '22px',
                      bgcolor: `${color}18`,
                      color: color,
                      border: `${lineWeight}px solid ${color}55`,
                      fontFamily: font,
                    }}
                  />
                );
              })}
            </Box>
          </Box>
        );

      case 'references':
        if (!savedReferences.length) return null;
        return (
          <Box className="resume28-section" mb={`${sectionSpacing}px`}>
            <SectionTitle>References</SectionTitle>
            <Divider sx={{ mb: `${paragraphSpacing * 0.5}px`, borderColor: color, borderBottomWidth: `${lineWeight}px` }} />
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: `${paragraphSpacing * 0.5}px 24px` }}>
              {savedReferences.map((ref, i) => {
                const name = typeof ref === 'string' ? ref : (ref.name || ref.referenceName || '');
                const title = typeof ref === 'object' ? (ref.title || ref.position || '') : '';
                const contact = typeof ref === 'object' ? (ref.email || ref.phone || ref.contact || '') : '';
                return (
                  <Box key={i} sx={{ minWidth: '160px', flex: '0 1 auto' }}>
                    <Typography sx={{ fontWeight: 700, fontSize: fontSize, lineHeight: `${lineSpacing}px`, fontFamily: font }}>{name}</Typography>
                    {title && <Typography sx={{ fontSize: fontSize, color: 'text.secondary', lineHeight: `${lineSpacing}px`, fontFamily: font }}>{title}</Typography>}
                    {contact && <Typography sx={{ fontSize: fontSize, color: 'text.secondary', lineHeight: `${lineSpacing}px`, fontFamily: font }}>{contact}</Typography>}
                  </Box>
                );
              })}
            </Box>
          </Box>
        );

      case 'volunteering':
        if (!savedVolunteering.length) return null;
        return (
          <Box className="resume28-section" mb={`${sectionSpacing}px`}>
            <SectionTitle>Volunteering</SectionTitle>
            <Divider sx={{ mb: `${paragraphSpacing * 0.5}px`, borderColor: color, borderBottomWidth: `${lineWeight}px` }} />
            {savedVolunteering.map((item, i) => {
              const role = typeof item === 'string' ? item : (item.subtopic || item.role || item.title || item.position || '');
              const org = typeof item === 'object' ? (item.organization || item.org || '') : '';
              const desc = typeof item === 'object' ? (item.content || item.description || '') : '';
              const date = typeof item === 'object' ? ([item.fromDate, item.toDate].filter(Boolean).join(' - ') || item.date || item.year || '') : '';
              return (
                <Box key={i} mb={`${paragraphSpacing}px`}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <Box sx={{ flex: 1 }}>
                      <Typography sx={{ fontWeight: 700, fontSize: fontSize, lineHeight: `${lineSpacing}px`, fontFamily: font, fontStyle: fontStyle }}>{role}</Typography>
                      {org && <Typography sx={{ fontSize: fontSize, color: 'text.secondary', lineHeight: `${lineSpacing}px`, fontFamily: font }}>{org}</Typography>}
                    </Box>
                    {date && (
                      <Typography sx={{ fontSize: fontSize, color: 'text.secondary', fontStyle: 'italic', ml: '8px', flexShrink: 0, fontFamily: font }}>{date}</Typography>
                    )}
                  </Box>
                  {desc && (
                    <Typography sx={{ fontSize: fontSize, lineHeight: `${lineSpacing}px`, mt: '2px', fontFamily: font, fontStyle: fontStyle, wordBreak: 'break-word' }}>{desc}</Typography>
                  )}
                </Box>
              );
            })}
          </Box>
        );

      case 'interests':
        if (!savedInterests.length) return null;
        return (
          <Box className="resume28-section" mb={`${sectionSpacing}px`}>
            <SectionTitle>Interests</SectionTitle>
            <Divider sx={{ mb: `${paragraphSpacing * 0.5}px`, borderColor: color, borderBottomWidth: `${lineWeight}px` }} />
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: `${paragraphSpacing * 0.3}px 16px` }}>
              {savedInterests.map((item, i) => {
                const text = typeof item === 'string' ? item : (item.interest || item.name || '');
                return (
                  <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <Box sx={{ width: '4px', height: '4px', borderRadius: '50%', bgcolor: color, flexShrink: 0 }} />
                    <Typography sx={{ fontSize: fontSize, lineHeight: `${lineSpacing}px`, fontFamily: font, fontStyle: fontStyle }}>{text}</Typography>
                  </Box>
                );
              })}
            </Box>
          </Box>
        );

      case 'websites':
        if (!savedWebsites.length) return null;
        return (
          <Box className="resume28-section" mb={`${sectionSpacing}px`}>
            <SectionTitle>Websites &amp; Links</SectionTitle>
            <Divider sx={{ mb: `${paragraphSpacing * 0.5}px`, borderColor: color, borderBottomWidth: `${lineWeight}px` }} />
            {savedWebsites.map((item, i) => {
              const label = typeof item === 'string' ? item : (item.label || item.name || item.title || '');
              const url = typeof item === 'object' ? (item.url || item.link || '') : '';
              return (
                <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: '4px', mb: '3px' }}>
                  <LanguageIcon sx={{ fontSize: fontSize, color, flexShrink: 0 }} />
                  <Typography sx={{ fontSize: fontSize, lineHeight: `${lineSpacing}px`, fontFamily: font, fontStyle: fontStyle, wordBreak: 'break-all' }}>
                    {label}{url && label !== url ? ` — ${url}` : ''}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        );

      default:
        if (id.startsWith('custom_')) {
          const name = id.replace('custom_', '');
          const items = customSections[name] || [];
          if (!items.length) return null;
          return (
            <Box className="resume28-section" mb={`${sectionSpacing}px`}>
              <SectionTitle>{name.replace(/_/g, ' ')}</SectionTitle>
              <Divider sx={{ mb: `${paragraphSpacing * 0.5}px`, borderColor: color, borderBottomWidth: `${lineWeight}px` }} />
              {items.map((item, i) => (
                <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', mb: '3px' }}>
                  <Box sx={{ width: '4px', height: '4px', borderRadius: '50%', bgcolor: color, flexShrink: 0, mt: '5.5px', mr: '6px' }} />
                  <Typography sx={{ fontSize: fontSize, lineHeight: `${lineSpacing}px`, fontFamily: font, fontStyle: fontStyle, wordBreak: 'break-word', flex: 1 }}>{item}</Typography>
                </Box>
              ))}
            </Box>
          );
        }
        return null;
    }
  };

  const pageStyle = {
    width: `${pageW}px`,
    minHeight: `${pageH}px`,
    maxWidth: `${pageW}px`,
    margin: '0 auto',
    backgroundColor: '#ffffff',
    boxSizing: 'border-box',
    fontFamily: font,
    fontStyle: fontStyle,
    fontSize: fontSize,
    color: '#222',
    padding: `${topBottomMargin}px ${sideMargins}px`,
    overflowX: 'hidden',
    wordBreak: 'break-word',
    ...(exporting ? { overflow: 'hidden', height: `${pageH}px` } : {}),
  };

  return (
    <Box sx={{ position: 'relative' }}>
      {/* Controls */}
      {!exporting && (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
          <IconButton
            onClick={() => setCurrentPage((p) => Math.max(0, p - 1))}
            disabled={currentPage === 0}
            size="small"
          >
            <ChevronLeft />
          </IconButton>
          <Typography variant="body2">
            Page {currentPage + 1} / {totalPages}
          </Typography>
          <IconButton
            onClick={() => setCurrentPage((p) => Math.min(totalPages - 1, p + 1))}
            disabled={currentPage >= totalPages - 1}
            size="small"
          >
            <ChevronRight />
          </IconButton>
          {enableDragDrop && (
            <>
              <Button variant="outlined" size="small" startIcon={<Reorder />} onClick={() => setArrangeDialogOpen(true)}>
                Arrange Sections
              </Button>
              <Button onClick={resetSectionOrder} variant="outlined" color="secondary" size="small">
                Reset All Sections
              </Button>
            </>
          )}
        </Box>
      )}

      {/* Hidden full content for height measurement */}
      <Box
        ref={measureRef}
        sx={{
          position: 'absolute',
          visibility: 'hidden',
          pointerEvents: 'none',
          width: `${pageW}px`,
          top: 0,
          left: 0,
          zIndex: -1,
          padding: `${topBottomMargin}px ${sideMargins}px`,
          boxSizing: 'border-box',
        }}
      >
        {sectionOrder.map((id) => (
          <React.Fragment key={id}>{renderSection(id)}</React.Fragment>
        ))}
      </Box>

      {/* A4 Page */}
      <Box
        component={Paper}
        elevation={exporting ? 0 : 3}
        className="resume-page"
        sx={{
          ...pageStyle,
          boxShadow: exporting ? 'none' : '0 4px 24px rgba(0,0,0,0.13)',
        }}
      >
        {/* Only show header on the first page */}
        {(currentPage === 0 || exporting) && renderSection('header')}

        {/* Content sections for current page */}
        {getSectionsForCurrentPage()
          .filter((id) => id !== 'header')
          .map((id) => (
            <React.Fragment key={id}>{renderSection(id)}</React.Fragment>
          ))}
      </Box>

      {/* Arrange Dialog */}
      <Dialog open={arrangeDialogOpen} onClose={() => setArrangeDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Arrange Resume Sections</DialogTitle>
        <DialogContent>
          <Box sx={{ minHeight: 300, pt: 1 }}>
            {sectionOrder.map((id, index) => (
              <Paper
                key={id}
                draggable
                onDragStart={(e) => handleDragStart(e, index)}
                onDragOver={(e) => handleDragOver(e, index)}
                onDrop={(e) => handleDrop(e, index)}
                onDragEnd={handleDragEnd}
                sx={{
                  p: 1.5,
                  mb: 1,
                  display: 'flex',
                  alignItems: 'center',
                  bgcolor: dragOverIndex === index ? '#f0f8ff' : 'white',
                  border: dragOverIndex === index ? `2px dashed ${color}` : '1px solid #ddd',
                  cursor: 'grab',
                  userSelect: 'none',
                  transition: 'all 0.15s ease',
                  '&:hover': { bgcolor: '#fafafa' },
                }}
              >
                <DragIndicator sx={{ mr: 2, color: '#999' }} />
                <Typography sx={{ flexGrow: 1, textTransform: 'capitalize', fontSize: '13px' }}>
                  {id.replace(/-/g, ' ').replace('custom_', '').replace(/_/g, ' ')}
                </Typography>
              </Paper>
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={resetSectionOrder} color="inherit">Reset Order</Button>
          <Button variant="contained" onClick={() => setArrangeDialogOpen(false)}>Done</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Resume28;